import { makeRequest } from './RequestService';

/**
 * Standard API response format used across the application
 * @template T - The type of the data payload in the response
 */
export type ApiResponse<T = unknown> = {
  status: number;
  message: string;
  data: T;
};

export class APIClient {
  private static instance: APIClient;

  private constructor(private baseURL: string) {}

  public static getInstance(baseURL: string): APIClient {
    if (!APIClient.instance) {
      APIClient.instance = new APIClient(baseURL);
    }
    return APIClient.instance;
  }

  private async request<T>(url: string, options: RequestInit, authRequired: boolean): Promise<T> {
    try {
      return await makeRequest<T>(this.baseURL, url, options, authRequired);
    } catch (error: any) {
      console.error(`API Request failed: ${url}`, {
        method: options.method,
        headers: options.headers,
        body: options.body,
        error: error.message || error
      });
      throw error;
    }
  }

  get<T>(url: string, customHeaders: Record<string, string> = {}, authRequired = true): Promise<T> {
    return this.request<T>(
      url,
      {
        method: 'GET',
        headers: customHeaders
      },
      authRequired
    );
  }

  post(url: string, data: unknown, customHeaders?: Record<string, string>, authRequired?: boolean): Promise<never>;
  post<T>(url: string, data: unknown, customHeaders?: Record<string, string>, authRequired?: boolean): Promise<T>;
  post<T>(url: string, data: unknown, customHeaders: Record<string, string> = {}, authRequired = true): Promise<T> {
    return this.request<T>(
      url,
      {
        method: 'POST',
        headers: customHeaders,
        body: JSON.stringify(data)
      },
      authRequired
    );
  }

  put<T>(url: string, data: unknown, customHeaders: Record<string, string> = {}, authRequired = true): Promise<T> {
    return this.request<T>(
      url,
      {
        method: 'PUT',
        headers: customHeaders,
        body: JSON.stringify(data)
      },
      authRequired
    );
  }

  patch<T>(url: string, data: unknown, customHeaders: Record<string, string> = {}, authRequired = true): Promise<T> {
    return this.request<T>(
      url,
      {
        method: 'PATCH',
        headers: customHeaders,
        body: JSON.stringify(data)
      },
      authRequired
    );
  }

  delete<T>(url: string, data?: unknown, customHeaders: Record<string, string> = {}, authRequired = true): Promise<T> {
    const requestInit: RequestInit = {
      method: 'DELETE',
      headers: customHeaders
    };

    if (data) {
      requestInit.body = JSON.stringify(data);
    }

    return this.request<T>(url, requestInit, authRequired);
  }
}

export const apiClient = APIClient.getInstance('/api');
